@if (showAlert) {
  <app-alert-modal
    [key]="alertKey"
    [isOpen]="showAlert"
    [title]="alertTitle"
    [type]="alertType"
    [message]="alertMessage"
    buttonText="Close"
    [isLoading]="isLoading"
    (closed)="showAlert = false"
  ></app-alert-modal>
}

<!-- HERO / TITLE -->
<section class="bg-white py-16">
  <div class="max-w-6xl mx-auto px-6 space-y-4">
    <h1 class="text-4xl font-bold text-gray-900">Contact AuditFixers</h1>

    <div class="space-y-2">
      <p class="text-lg text-gray-600">
        Need help with ISO 27001 and ISO 42001 compliance, certification readiness, or gap assessments?
      </p>

      <p class="text-lg text-gray-600">
        The AuditFixers team is ready to support you.
      </p>
    </div>
  </div>
</section>

<!-- FORM + IMAGE -->
<section class="bg-white py-16">
  <div class="max-w-6xl mx-auto px-6">

    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">

      <!-- IMAGE -->
      <div class="flex justify-center items-start min-h-[500px]">
        <img
          src="assets/contact_us_image.png"
          alt="Contact AuditFixers"
          class="w-full h-auto object-contain"
        />
      </div>

      <!-- FORM -->
      <div>

        <div class="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
          <p class="text-sm text-blue-700 font-medium">
            📩 Tell us about your requirements and we’ll get back to you within 24 hours.
          </p>
        </div>

        <form class="space-y-6" (ngSubmit)="onSubmit()" #contactForm="ngForm">

          <!-- Package Selection -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700" for="messageType">
              Package
            </label>

            <select
              id="messageType"
              [(ngModel)]="formData.messageType"
              name="messageType"
              class="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              @for (type of messageTypes; track type.value) {
                <option [value]="type.value">
                  {{ type.label }}
                </option>
              }

              <option value="others">Others</option>
            </select>
          </div>

          <!-- Custom Package -->
          @if (formData.messageType === -1) {
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700" for="customMessageType">
                Please specify your package
              </label>

              <input
                id="customMessageType"
                type="text"
                [(ngModel)]="formData.customMessageType"
                name="customMessageType"
                placeholder="Enter package name"
                class="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          }

          <!-- Company Name -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700" for="companyName">
              Company Name
            </label>

            <input
              id="companyName"
              type="text"
              name="companyName"
              [(ngModel)]="formData.companyName"
              (input)="onCompanyInput($event)"
              required
              placeholder="Company Name"
              class="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <!-- Full Name -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700" for="name">
              Full Name
            </label>

            <input
              id="name"
              type="text"
              name="name"
              [(ngModel)]="formData.name"
              (input)="onNameInput($event)"
              required
              placeholder="Your Full Name"
              class="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <!-- Email -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700" for="email">
              Email Address
            </label>

            <input
              id="email"
              type="email"
              name="email"
              [(ngModel)]="formData.email"
              (input)="onEmailInput($event)"
              required
              placeholder="you@example.com"
              class="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />

            @if (emailInvalid) {
              <div class="text-red-500 text-sm mt-1">
                Please enter a valid email.
              </div>
            }
          </div>

          <!-- Contact Number -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700" for="contactNumber">
              Contact Number
            </label>

            <input
              id="contactNumber"
              type="text"
              name="contactNumber"
              [(ngModel)]="formData.contactNumber"
              (input)="onContactNumberInput($event)"
              required
              placeholder="+353 XX XXX XXXX"
              class="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <!-- Message -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700" for="message">
              Message
            </label>

            <textarea
              id="message"
              name="message"
              rows="5"
              [(ngModel)]="formData.message"
              required
              placeholder="Tell AuditFixers how we can help you"
              class="w-full border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            ></textarea>
          </div>

          <!-- Submit Button -->
          <div>
            <button
              type="submit"
              [disabled]="loading || hasErrors"
              class="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-60 w-full"
            >
              {{ loading ? 'Sending...' : 'Send Message to AuditFixers' }}
            </button>

            <p class="text-xs text-gray-500 text-center mt-2">
              We typically respond within 24 hours.
            </p>
          </div>

          <!-- Alerts -->
          <div class="space-y-2">

            @if (success) {
              <div class="p-4 bg-green-100 text-green-700 rounded-lg">
                ✅ Your message has been sent successfully! We’ll get back to you shortly.
              </div>
            }

            @if (error) {
              <div class="p-4 bg-red-100 text-red-700 rounded-lg">
                ❌ Something went wrong while contacting AuditFixers. Please try again.
              </div>
            }

          </div>

        </form>
      </div>

    </div>
  </div>
</section>

<!-- CONTACT DETAILS -->
<section class="bg-gray-50 py-12">
  <div class="max-w-3xl mx-auto px-6 space-y-8 text-center text-gray-700">
    <p class="text-lg">
      Prefer direct contact? Reach out to AuditFixers using the details below.
    </p>

    <div class="space-y-4">
      <p class="font-semibold text-gray-800">
        Email:
        <a href="mailto:hello@auditfixers.com" class="text-blue-600 hover:underline">
          info@auditfixers.com
        </a>
      </p>

      <p class="font-semibold text-gray-800">
        Address:
        <span class="text-gray-600">
          Dogpatch Labs, The CHQ Building, Custom House Quay, Dublin 1, D01 Y6H7, Ireland
        </span>
      </p>
    </div>
  </div>
</section>